import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, apiFetch, obtenerBlobUrl, subirBinario } from '../lib/api';
import { Modal } from '../components/Modal';
import { useConfirm } from '../components/ConfirmDialog';
import ChatAndres from './ChatAndres';

/** Wave 3: pantalla de invitación cuando el usuario aún no conecta su cuenta Microsoft. */
export function ConectarMicrosoft({ contexto }: { contexto: string }) {
  const [ocupado, setOcupado] = useState(false);
  return (
    <div className="tarjeta flex flex-col items-center gap-4 px-6 py-14 text-center">
      <span aria-hidden="true" className="flex h-14 w-14 items-center justify-center rounded-2xl bg-celeste/25 text-2xl">🔐</span>
      <div>
        <h2 className="text-[16px] font-medium text-azul-noche">Conecta tu cuenta Microsoft</h2>
        <p className="mx-auto mt-1.5 max-w-md text-[13px] font-normal leading-relaxed text-gris">
          {contexto} con <strong className="font-medium">tus permisos reales de SharePoint</strong>: ves
          exactamente lo que tu cuenta corporativa puede ver, ni más ni menos.
        </p>
      </div>
      <button
        type="button"
        disabled={ocupado}
        onClick={async () => {
          setOcupado(true);
          try {
            const { url } = await apiFetch<{ url: string }>('/api/msauth/authorize');
            window.location.href = url;
          } catch { setOcupado(false); }
        }}
        className="btn-primario disabled:opacity-50"
      >
        {ocupado ? 'Redirigiendo…' : 'Conectar mi cuenta Microsoft'}
      </button>
      <p className="text-[11px] font-light text-gris">Un solo paso, desde Mi Perfil también. Usa el mismo correo de esta plataforma.</p>
    </div>
  );
}

/**
 * AMBEC SharePoint (2026-07-21): explorador PROFESIONAL de todo el sitio — navegación
 * completa desde la raíz de Documentos, búsqueda global, vistas lista/grilla y orden por
 * nombre/fecha/tamaño. Solo lectura (proxy API; el token Graph jamás llega al navegador);
 * "Ver" trae el binario por el proxy y "Abrir" va a SharePoint web.
 */

interface Item {
  id?: string;
  nombre: string;
  tipo: 'carpeta' | 'archivo';
  ruta: string | null;
  size: number | null;
  modificado?: string | null;
  webUrl?: string | null;
}

type Vista = 'lista' | 'grilla';
type Orden = 'nombre' | 'fecha' | 'tamano';

const ICONO: Record<string, string> = {
  pdf: '📕', docx: '📘', doc: '📘', xlsx: '📗', xls: '📗', csv: '📗', pptx: '📙', ppt: '📙',
  jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', kmz: '🗺️', kml: '🗺️', shp: '🗺️', dbf: '🗺️', dwg: '📐',
  zip: '🗜️', rar: '🗜️', msg: '✉️', eml: '✉️', txt: '📄', md: '📄',
};
const iconoDe = (n: string) => ICONO[n.toLowerCase().split('.').pop() ?? ''] ?? '📄';

const tam = (n: number | null) => {
  if (n === null || n === 0) return '—';
  if (n >= 1_073_741_824) return `${(n / 1_073_741_824).toFixed(1)} GB`;
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

const fecha = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const dias = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 365) return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export default function NavegadorSharePoint() {
  const confirmar = useConfirm();
  const [ruta, setRuta] = useState('');
  const [items, setItems] = useState<Item[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState<Item[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [vista, setVista] = useState<Vista>(() => (localStorage.getItem('sp-vista') as Vista) || 'lista');
  const [orden, setOrden] = useState<Orden>('nombre');
  const [asc, setAsc] = useState(true);
  const [viendo, setViendo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [creandoCarpeta, setCreandoCarpeta] = useState(false);
  const [nombreCarpeta, setNombreCarpeta] = useState('');
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const [nuevoNombre, setNuevoNombre] = useState('');
  // Modal crítico de eliminación (2026-07-22): para habilitar el botón rojo hay que
  // ESCRIBIR el nombre exacto del elemento (estilo GitHub) — no basta un check.
  const [eliminando, setEliminando] = useState<Item | null>(null);
  const [nombreConfirm, setNombreConfirm] = useState('');
  const [borrandoSp, setBorrandoSp] = useState(false);
  // 🤖 chat con Andrés enfocado en UN archivo (chat completo en modal, todas las skills).
  const [preguntandoRuta, setPreguntandoRuta] = useState<string | null>(null);
  const [chatArchivo, setChatArchivo] = useState<{
    chatId: string; favoritoId: string; favoritoNombre: string; nombre: string;
    enCorpus: boolean; adjunto: { nombre: string; storage_path: string } | null; ruta: string;
  } | null>(null);
  const inputSubir = useRef<HTMLInputElement>(null);
  // Favoritas persistentes POR USUARIO en la base (migración 0045). Al primer uso se
  // migran las que hubiera en localStorage (implementación anterior) y se limpia la key.
  const [favoritos, setFavoritos] = useState<{ nombre: string; ruta: string }[]>([]);
  useEffect(() => {
    void (async () => {
      try {
        let { favoritas } = await apiFetch<{ favoritas: { nombre: string; ruta: string }[] }>('/api/sharepoint/carpetas-favoritas');
        const locales: { nombre: string; ruta: string }[] = (() => {
          try { return JSON.parse(localStorage.getItem('sp-favoritos') ?? '[]'); } catch { return []; }
        })();
        if (locales.length > 0) {
          for (const f of locales) {
            if (!favoritas.some((x) => x.ruta === f.ruta)) {
              await apiFetch('/api/sharepoint/carpetas-favoritas', { method: 'POST', body: JSON.stringify(f) }).catch(() => undefined);
            }
          }
          localStorage.removeItem('sp-favoritos');
          ({ favoritas } = await apiFetch<{ favoritas: { nombre: string; ruta: string }[] }>('/api/sharepoint/carpetas-favoritas'));
        }
        setFavoritos(favoritas);
      } catch { /* sin favoritas no se bloquea el explorador */ }
    })();
  }, []);

  const esFavorita = (ruta: string | null) => ruta !== null && favoritos.some((f) => f.ruta === ruta);
  const toggleFavorita = (it: Item) => {
    if (it.ruta === null) return;
    const quitar = favoritos.some((f) => f.ruta === it.ruta);
    if (!quitar && favoritos.length >= 20) { setAviso('Máximo 20 carpetas favoritas.'); return; }
    // Optimista: la UI cambia al tiro y la base se actualiza detrás.
    setFavoritos((prev) => quitar ? prev.filter((f) => f.ruta !== it.ruta) : [...prev, { nombre: it.nombre, ruta: it.ruta! }]);
    void (quitar
      ? apiFetch(`/api/sharepoint/carpetas-favoritas?ruta=${encodeURIComponent(it.ruta)}`, { method: 'DELETE' })
      : apiFetch('/api/sharepoint/carpetas-favoritas', { method: 'POST', body: JSON.stringify({ nombre: it.nombre, ruta: it.ruta }) })
    ).catch(() => setAviso('No se pudo guardar el cambio de favoritas.'));
  };

  const cambiarVista = (v: Vista) => { setVista(v); localStorage.setItem('sp-vista', v); };

  const [requiereMs, setRequiereMs] = useState(false);

  const rutaRef = useRef('');
  const navegar = useCallback(async (r: string) => {
    setCargando(true);
    setError(null);
    setResultados(null);
    try {
      const d = await apiFetch<{ items: Item[] }>(`/api/sharepoint/navegar?ruta=${encodeURIComponent(r)}`);
      setItems(d.items);
      setRuta(r);
      rutaRef.current = r;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ms_no_conectado') { setRequiereMs(true); return; }
      setError(e instanceof Error ? e.message : 'No se pudo leer el SharePoint.');
    } finally {
      setCargando(false);
    }
  }, []);

  /** Tras un CRUD: Graph tarda 1-3 s en reflejar cambios ⇒ refresco inmediato + re-refrescos
   *  SILENCIOSOS (sin skeleton) hasta que el listado se ponga al día. */
  const refrescarTrasCambio = useCallback(async (r: string) => {
    await navegar(r);
    for (const ms of [1600, 3800]) {
      window.setTimeout(async () => {
        if (rutaRef.current !== r) return; // el usuario ya navegó a otra parte
        try {
          const d = await apiFetch<{ items: Item[] }>(`/api/sharepoint/navegar?ruta=${encodeURIComponent(r)}`);
          if (rutaRef.current === r) setItems(d.items);
        } catch { /* silencioso */ }
      }, ms);
    }
  }, [navegar]);

  useEffect(() => { void navegar(''); }, [navegar]);

  async function buscar() {
    if (busqueda.trim().length < 2) return;
    setBuscando(true);
    setError(null);
    try {
      const d = await apiFetch<{ items: Item[] }>(`/api/sharepoint/buscar?q=${encodeURIComponent(busqueda.trim())}`);
      setResultados(d.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La búsqueda falló.');
    } finally {
      setBuscando(false);
    }
  }

  async function verArchivo(it: Item) {
    const clave = it.id ?? it.ruta ?? it.nombre;
    setViendo(clave);
    try {
      const qs = it.id ? `id=${encodeURIComponent(it.id)}` : `ruta=${encodeURIComponent(it.ruta ?? '')}`;
      const url = await obtenerBlobUrl(`/api/sharepoint/archivo?${qs}`);
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo abrir el archivo.');
    } finally {
      setViendo(null);
    }
  }

  /** Escritura fuera de "Proyectos en desarrollo" ⇒ confirmación reforzada. */
  const zonaSegura = () => (resultados === null && (ruta === 'Proyectos en desarrollo' || ruta.startsWith('Proyectos en desarrollo/')));
  const confirmarZona = async (accion: string) =>
    zonaSegura() || (await confirmar({ titulo: 'Fuera de "Proyectos en desarrollo"', mensaje: `Estás FUERA de "Proyectos en desarrollo" (${ruta || 'raíz del sitio'}). ¿${accion} aquí de todas formas?`, textoAceptar: accion, destructivo: true }));

  const avisar = (msg: string) => { setAviso(msg); window.setTimeout(() => setAviso(null), 7000); };
  const conNotifica = (base: string, notificados?: string[]) =>
    avisar(notificados?.length ? `${base} · 📚 Andrés estudiará: ${notificados.join(', ')}` : base);

  async function subirArchivos(archivos: FileList | File[]) {
    if (!(await confirmarZona('Subir archivos'))) return;
    setError(null);
    let ultNotif: string[] = [];
    for (const f of Array.from(archivos).slice(0, 10)) {
      setSubiendo(f.name);
      try {
        const r = await subirBinario<{ reemplazo: boolean; notificados: string[] }>(
          `/api/sharepoint/archivos?ruta=${encodeURIComponent(ruta)}&nombre=${encodeURIComponent(f.name)}`, f);
        ultNotif = r.notificados;
      } catch (e) {
        setError(`${f.name}: ${e instanceof Error ? e.message : 'no se pudo subir'}`);
        break;
      }
    }
    setSubiendo(null);
    conNotifica('✓ Subida completa', ultNotif);
    await refrescarTrasCambio(ruta);
  }

  async function crearCarpeta() {
    const nombre = nombreCarpeta.trim();
    if (!nombre || !(await confirmarZona('Crear una carpeta'))) return;
    setCreandoCarpeta(false);
    setNombreCarpeta('');
    try {
      await apiFetch('/api/sharepoint/carpetas', { method: 'POST', body: JSON.stringify({ ruta, nombre }) });
      avisar(`✓ Carpeta "${nombre}" creada`);
      await refrescarTrasCambio(ruta);
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo crear la carpeta.'); }
  }

  async function renombrarItem(it: Item) {
    const nombre = nuevoNombre.trim();
    setRenombrando(null);
    if (!nombre || nombre === it.nombre || it.ruta === null) return;
    if (!(await confirmarZona('Renombrar'))) return;
    try {
      const r = await apiFetch<{ notificados: string[] }>('/api/sharepoint/archivos', { method: 'PATCH', body: JSON.stringify({ ruta: it.ruta, nuevoNombre: nombre }) });
      conNotifica(`✓ Renombrado a "${nombre}"`, r.notificados);
      await refrescarTrasCambio(ruta);
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo renombrar.'); }
  }

  async function eliminarItem(it: Item) {
    // La zona y el alcance ya se advirtieron DENTRO del modal; aquí solo se ejecuta.
    if (it.ruta === null) return;
    setBorrandoSp(true);
    try {
      const r = await apiFetch<{ notificados: string[] }>(`/api/sharepoint/archivos?ruta=${encodeURIComponent(it.ruta)}`, { method: 'DELETE' });
      conNotifica(`🗑 "${it.nombre}" enviado a la papelera de SharePoint`, r.notificados);
      if (esFavorita(it.ruta)) toggleFavorita(it);
      setEliminando(null);
      await refrescarTrasCambio(ruta);
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo eliminar.'); }
    finally { setBorrandoSp(false); }
  }

  const migas = ruta ? ruta.split('/') : [];
  const lista = useMemo(() => {
    const base = [...(resultados ?? items)];
    const dir = asc ? 1 : -1;
    base.sort((a, b) => {
      if (a.tipo !== b.tipo) return a.tipo === 'carpeta' ? -1 : 1; // carpetas primero, siempre
      if (orden === 'nombre') return dir * a.nombre.localeCompare(b.nombre, 'es');
      if (orden === 'tamano') return dir * ((a.size ?? 0) - (b.size ?? 0));
      return dir * (new Date(a.modificado ?? 0).getTime() - new Date(b.modificado ?? 0).getTime());
    });
    return base;
  }, [items, resultados, orden, asc]);

  const abrirItem = (it: Item) => {
    if (it.tipo === 'carpeta') { if (it.ruta !== null) void navegar(it.ruta); }
    else void verArchivo(it);
  };

  const BotonOrden = ({ campo, label }: { campo: Orden; label: string }) => (
    <button
      type="button"
      onClick={() => { if (orden === campo) setAsc(!asc); else { setOrden(campo); setAsc(true); } }}
      className={`flex items-center gap-0.5 text-[11px] font-medium uppercase tracking-wide transition-colors ${orden === campo ? 'text-azul-profundo' : 'text-gris hover:text-azul-noche'}`}
    >
      {label}{orden === campo && <span aria-hidden="true" className="text-[9px]">{asc ? '▲' : '▼'}</span>}
    </button>
  );

  const Estrella = ({ it }: { it: Item }) => it.tipo === 'carpeta' && it.ruta !== null ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggleFavorita(it); }}
      className={`rounded px-1.5 py-0.5 text-[13px] transition-colors ${esFavorita(it.ruta) ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`}
      title={esFavorita(it.ruta) ? 'Quitar de favoritas' : 'Marcar carpeta como favorita'}
      aria-label={`${esFavorita(it.ruta) ? 'Quitar' : 'Marcar'} ${it.nombre} como favorita`}
    >
      ★
    </button>
  ) : null;

  const copiarRuta = async (it: Item) => {
    if (it.ruta === null) return;
    try {
      await navigator.clipboard.writeText(it.ruta);
      avisar(`📋 Ruta copiada — pégasela a Andrés en el chat: "${it.ruta.length > 60 ? `…${it.ruta.slice(-57)}` : it.ruta}"`);
    } catch { setError('No se pudo copiar al portapapeles.'); }
  };

  async function abrirPreguntar(it: Item) {
    if (it.ruta === null && !it.id) return;
    setPreguntandoRuta(it.ruta ?? it.id ?? null);
    try {
      // Se manda ruta E id: el backend baja el binario prefiriendo el ID (como "Ver") —
      // la ruta derivada de una búsqueda puede no direccionar bien en Graph.
      const d = await apiFetch<{ chatId: string; favoritoId: string; favoritoNombre: string; enCorpus: boolean; adjunto: { nombre: string; storage_path: string } | null; ruta: string }>(
        '/api/sharepoint/preguntar-archivo',
        { method: 'POST', body: JSON.stringify({ ruta: it.ruta, id: it.id ?? null, nombre: it.nombre }) },
      );
      setChatArchivo({ ...d, nombre: it.nombre });
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo abrir el chat sobre el archivo.'); }
    finally { setPreguntandoRuta(null); }
  }

  const Gestion = ({ it }: { it: Item }) => (it.ruta === null && !it.id) ? null : (
    <span className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
      {it.tipo === 'archivo' && (
        <button type="button" onClick={() => void abrirPreguntar(it)} disabled={preguntandoRuta === (it.ruta ?? it.id)} className="rounded px-1.5 py-0.5 text-[12px] text-gris transition-colors hover:text-azul-profundo disabled:opacity-50" title="Preguntarle a Andrés sobre este archivo">
          {preguntandoRuta === (it.ruta ?? it.id) ? '…' : '🤖'}
        </button>
      )}
      {it.ruta !== null && (<>
      <button type="button" onClick={() => void copiarRuta(it)} className="rounded px-1.5 py-0.5 text-[11.5px] text-gris transition-colors hover:text-azul-profundo" title="Copiar la ruta completa (para pegársela a Andrés)">📋</button>
      <button type="button" onClick={() => { setRenombrando(it.ruta); setNuevoNombre(it.nombre); }} className="rounded px-1.5 py-0.5 text-[11.5px] text-gris transition-colors hover:text-azul-profundo" title="Renombrar">✎</button>
      <button type="button" onClick={() => { setNombreConfirm(''); setEliminando(it); }} className="rounded px-1.5 py-0.5 text-[11.5px] text-gris transition-colors hover:text-red-600" title="Eliminar (va a la papelera de SharePoint)">🗑</button>
      </>)}
    </span>
  );

  const Acciones = ({ it }: { it: Item }) => it.tipo === 'archivo' ? (
    <span className="flex shrink-0 items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => void verArchivo(it)} disabled={viendo === (it.id ?? it.ruta ?? it.nombre)} className="rounded-lg px-2 py-1 text-[11.5px] font-medium text-azul-profundo transition-colors hover:bg-celeste/15 disabled:opacity-60" title="Ver aquí (proxy seguro)">
      {viendo === (it.id ?? it.ruta ?? it.nombre) ? '…' : 'Ver'}
      </button>
      {it.webUrl && (
        <a href={it.webUrl} target="_blank" rel="noopener noreferrer" className="rounded-lg px-2 py-1 text-[11.5px] font-medium text-gris transition-colors hover:bg-celeste/15 hover:text-azul-profundo" title="Abrir en SharePoint">
          Abrir
        </a>
      )}
    </span>
  ) : null;

  if (requiereMs) return <ConectarMicrosoft contexto="El explorador de SharePoint navega" />;

  return (
    <div className="flex flex-col gap-3">
      {/* ── Barra superior: breadcrumb + búsqueda + controles ── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <nav aria-label="Ruta" className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-[12.5px] font-normal text-gris">
          <button type="button" onClick={() => void navegar('')} className={`rounded px-1.5 py-0.5 hover:bg-celeste/20 ${!ruta && !resultados ? 'font-medium text-azul-profundo' : ''}`}>
            AMBECSpA
          </button>
          {migas.map((m, i) => (
            <span key={i} className="flex min-w-0 items-center gap-1">
              <span aria-hidden="true" className="text-gris/50">›</span>
              <button type="button" onClick={() => void navegar(migas.slice(0, i + 1).join('/'))} className={`truncate rounded px-1.5 py-0.5 hover:bg-celeste/20 ${i === migas.length - 1 ? 'font-medium text-azul-profundo' : ''}`}>
                {m}
              </button>
            </span>
          ))}
          {resultados !== null && <span className="ml-1 text-[11.5px] font-light">· {resultados.length} resultado{resultados.length === 1 ? '' : 's'} en todo el sitio <button type="button" onClick={() => setResultados(null)} className="font-medium text-azul-profundo hover:underline">Limpiar</button></span>}
        </nav>

        <form onSubmit={(e) => { e.preventDefault(); void buscar(); }} className="relative w-full sm:w-72">
          <svg viewBox="0 0 24 24" className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gris/60" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar en todo el SharePoint…"
            className="campo !h-9 w-full !pl-8 !text-[13px]"
            aria-label="Buscar en todo el SharePoint"
          />
          {buscando && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gris">buscando…</span>}
        </form>

        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => inputSubir.current?.click()} disabled={subiendo !== null || resultados !== null} className="btn-secundario !h-9 !px-3 !text-[12.5px] disabled:opacity-50" title="Subir archivos a esta carpeta (o arrástralos)">
            ↑ Subir
          </button>
          <input ref={inputSubir} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) void subirArchivos(e.target.files); e.target.value = ''; }} />
          <button type="button" onClick={() => setCreandoCarpeta(true)} disabled={resultados !== null} className="btn-fantasma !h-9 !px-3 !text-[12.5px] disabled:opacity-50">+ Carpeta</button>
        </div>
        <div className="flex items-center gap-0.5 rounded-lg border border-slate-200 p-0.5" role="group" aria-label="Vista">
          <button type="button" onClick={() => cambiarVista('lista')} className={`rounded-md p-1.5 transition-colors ${vista === 'lista' ? 'bg-celeste/25 text-azul-profundo' : 'text-gris hover:text-azul-noche'}`} title="Vista de lista" aria-pressed={vista === 'lista'}>
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>
          </button>
          <button type="button" onClick={() => cambiarVista('grilla')} className={`rounded-md p-1.5 transition-colors ${vista === 'grilla' ? 'bg-celeste/25 text-azul-profundo' : 'text-gris hover:text-azul-noche'}`} title="Vista de grilla" aria-pressed={vista === 'grilla'}>
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
          </button>
        </div>
      </div>

      {favoritos.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-gris">Favoritas</span>
          {favoritos.map((f) => (
            <span key={f.ruta} className="group/fav flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50/60 py-1 pl-2.5 pr-1.5 transition-colors hover:border-amber-300">
              <button type="button" onClick={() => void navegar(f.ruta)} className="flex items-center gap-1 text-[12px] font-normal text-azul-noche" title={f.ruta}>
                <span aria-hidden="true" className="text-[11px] text-amber-500">★</span>
                {f.nombre.length > 28 ? f.nombre.slice(0, 26) + '…' : f.nombre}
              </button>
              <button type="button" onClick={() => toggleFavorita({ nombre: f.nombre, ruta: f.ruta, tipo: 'carpeta', size: null })} className="rounded-full px-1 text-[10px] text-gris/0 transition-colors hover:text-red-600 group-hover/fav:text-gris" aria-label={`Quitar ${f.nombre} de favoritas`}>✕</button>
            </span>
          ))}
        </div>
      )}

      {aviso && <p role="status" className="rounded-xl border border-celeste/60 bg-[#F2F9FD] px-4 py-2 text-[12.5px] font-normal text-azul-noche">{aviso}</p>}
      {subiendo && <p role="status" className="flex items-center gap-2 text-[12px] font-normal text-gris"><span className="h-3 w-3 animate-spin rounded-full border-2 border-celeste border-t-azul-profundo" /> Subiendo {subiendo}…</p>}
      {creandoCarpeta && (
        <div className="flex items-center gap-2">
          <input autoFocus value={nombreCarpeta} onChange={(e) => setNombreCarpeta(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void crearCarpeta(); if (e.key === 'Escape') setCreandoCarpeta(false); }} placeholder="Nombre de la nueva carpeta…" className="campo !h-9 w-72 !text-[13px]" aria-label="Nombre de la nueva carpeta" />
          <button type="button" onClick={() => void crearCarpeta()} disabled={!nombreCarpeta.trim()} className="btn-primario !h-9 !px-3 !text-[12.5px] disabled:opacity-50">Crear</button>
          <button type="button" onClick={() => setCreandoCarpeta(false)} className="btn-fantasma !h-9 !px-3 !text-[12.5px]">Cancelar</button>
        </div>
      )}
      {error && <p role="alert" className="alerta-error">{error}</p>}

      {/* ── Contenido ── */}
      <section
        className={`tarjeta relative overflow-hidden ${arrastrando ? 'ring-2 ring-azul-profundo/60' : ''}`}
        onDragOver={(e) => { if (resultados === null) { e.preventDefault(); setArrastrando(true); } }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => { e.preventDefault(); setArrastrando(false); if (resultados === null && e.dataTransfer.files.length) void subirArchivos(e.dataTransfer.files); }}
      >
        {arrastrando && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-celeste/20 text-sm font-medium text-azul-profundo">
            Suelta para subir a {ruta ? `"${ruta.split('/').pop()}"` : 'la raíz'}
          </div>
        )}
        {vista === 'lista' && (
          <div className="hidden grid-cols-[minmax(0,1fr)_7rem_5.5rem_7.5rem] items-center gap-2 border-b border-slate-100 bg-[#FAFBFD] px-4 py-2 sm:grid">
            <BotonOrden campo="nombre" label="Nombre" />
            <BotonOrden campo="fecha" label="Modificado" />
            <BotonOrden campo="tamano" label="Tamaño" />
            <span />
          </div>
        )}

        {cargando || buscando ? (
          <div className="flex flex-col gap-2 p-4">
            {[...Array(7)].map((_, i) => <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-100" />)}
          </div>
        ) : lista.length === 0 ? (
          <p className="p-10 text-center text-sm font-light text-gris">{resultados !== null ? 'Sin resultados para esa búsqueda.' : 'Carpeta vacía.'}</p>
        ) : vista === 'lista' ? (
          <ul className="divide-y divide-slate-50">
            {lista.map((it) => (
              <li key={it.id ?? it.ruta ?? it.nombre}>
                {/* div role=button (no <button>): adentro van botones de acción reales y
                    anidar <button> es DOM inválido (clicks erráticos — bug del 22-jul). */}
                <div role="button" tabIndex={0} onClick={() => abrirItem(it)} onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) abrirItem(it); }} className="group/fila grid w-full cursor-pointer grid-cols-[minmax(0,1fr)] items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-celeste/8 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo sm:grid-cols-[minmax(0,1fr)_7rem_5.5rem_7.5rem]">
                  <span className="flex min-w-0 items-center gap-2.5">
                    {it.tipo === 'carpeta' ? (
                      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] shrink-0 text-azul-medio" fill="currentColor" opacity="0.85" aria-hidden="true"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                    ) : (
                      <span aria-hidden="true" className="w-[18px] shrink-0 text-center text-[15px] leading-none">{iconoDe(it.nombre)}</span>
                    )}
                    <span className="min-w-0 flex-1">
                      {it.ruta !== null && renombrando === it.ruta ? (
                        <input
                          autoFocus
                          value={nuevoNombre}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setNuevoNombre(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') void renombrarItem(it); if (e.key === 'Escape') setRenombrando(null); }}
                          onBlur={() => void renombrarItem(it)}
                          className="campo !h-7 w-full max-w-md !text-[12.5px]"
                          aria-label={`Nuevo nombre para ${it.nombre}`}
                        />
                      ) : (
                      <span className={`block truncate text-[13px] ${it.tipo === 'carpeta' ? 'font-medium text-azul-noche' : 'font-normal text-texto'}`}>{it.nombre}</span>
                      )}
                      {resultados !== null && it.ruta && <span className="block truncate text-[10.5px] font-light text-gris">{it.ruta.split('/').slice(0, -1).join(' › ')}</span>}
                    </span>
                  </span>
                  <span className="hidden text-[12px] font-light text-gris sm:block">{fecha(it.modificado)}</span>
                  <span className="hidden text-[12px] font-light tabular-nums text-gris sm:block">{it.tipo === 'carpeta' ? '—' : tam(it.size)}</span>
                  <span className="hidden items-center justify-end sm:flex"><Estrella it={it} /><Acciones it={it} /><Gestion it={it} /></span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {lista.map((it) => (
              <li key={it.id ?? it.ruta ?? it.nombre}>
                <div role="button" tabIndex={0} onClick={() => abrirItem(it)} onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) abrirItem(it); }} className="group/fila relative flex w-full cursor-pointer flex-col items-center gap-1.5 rounded-xl border border-transparent p-3 text-center transition-colors hover:border-celeste/60 hover:bg-celeste/8 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo">
                  {it.tipo === 'carpeta' && <span className="absolute right-1.5 top-1.5"><Estrella it={it} /></span>}
                  {it.tipo === 'carpeta' ? (
                    <svg viewBox="0 0 24 24" className="h-10 w-10 text-azul-medio" fill="currentColor" opacity="0.8" aria-hidden="true"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                  ) : (
                    <span aria-hidden="true" className="text-[34px] leading-none">{iconoDe(it.nombre)}</span>
                  )}
                  <span className="line-clamp-2 w-full break-words text-[11.5px] font-normal leading-snug text-texto">{it.nombre}</span>
                  <span className="text-[10px] font-light text-gris">{it.tipo === 'carpeta' ? fecha(it.modificado) : `${tam(it.size)} · ${fecha(it.modificado)}`}</span>
                  {/* Acciones también en grilla (antes solo la lista las tenía — el 🤖 no existía aquí) */}
                  <span className="flex items-center justify-center"><Acciones it={it} /><Gestion it={it} /></span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-[10.5px] font-light text-gris">Solo lectura · sitio AMBECSpA › Documentos · "Ver" abre el archivo aquí mismo; "Abrir" va a SharePoint.</p>

      {/* Spinner mientras se prepara el chat del 🤖 (crear chat + resolver ruta real +
          posible descarga del binario tarda unos segundos — sin esto quedaba un vacío). */}
      {preguntandoRuta !== null && !chatArchivo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-azul-noche/30" aria-hidden="true" />
          <div className="relative flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3.5 shadow-xl">
            <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-azul-profundo border-t-transparent" />
            <p className="text-[13px] font-normal text-texto">🤖 Preparando el chat con Andrés…</p>
          </div>
        </div>
      )}

      {/* 🤖 Chat COMPLETO con Andrés sobre un archivo puntual: overlay grande propio
          (el Modal estándar queda chico para un chat). Todas las skills disponibles. */}
      {chatArchivo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-5">
          <div className="fixed inset-0 bg-azul-noche/50" onClick={() => setChatArchivo(null)} aria-hidden="true" />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Preguntarle a Andrés sobre ${chatArchivo.nombre}`}
            className="relative flex h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5">
              <p className="min-w-0 truncate text-[13px] font-medium text-azul-noche">
                🤖 Andrés sobre <span className="font-normal">{chatArchivo.nombre}</span>
                <span className="ml-2 text-[11px] font-light text-gris">
                  {chatArchivo.enCorpus ? `· en el corpus de ${chatArchivo.favoritoNombre}` : '· adjunto al chat (fuera del corpus)'}
                </span>
              </p>
              <button
                type="button"
                onClick={() => setChatArchivo(null)}
                aria-label="Cerrar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gris transition-colors hover:bg-slate-100 hover:text-azul-profundo"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
              <ChatAndres
                favoritoId={chatArchivo.favoritoId}
                nombre={chatArchivo.favoritoNombre}
                onVolver={() => setChatArchivo(null)}
                chatInicialId={chatArchivo.chatId}
                adjuntoInicial={chatArchivo.adjunto}
                textoInicial={`Sobre el archivo "${chatArchivo.ruta}": `}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal crítico de eliminación: DOBLE CHECK — el botón rojo solo se habilita
          con el checkbox marcado. Zona y alcance advertidos aquí mismo. */}
      {eliminando && (
        <Modal titulo="Eliminar de SharePoint" onCerrar={() => { if (!borrandoSp) setEliminando(null); }} bloqueante>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-3.5 py-3">
              <span aria-hidden="true" className="text-xl">{eliminando.tipo === 'carpeta' ? '📁' : '📄'}</span>
              <div className="min-w-0">
                <p className="truncate text-[14px] font-medium text-azul-noche">{eliminando.nombre}</p>
                <p className="truncate text-[11.5px] font-light text-gris">{(eliminando.ruta ?? '').split('/').slice(0, -1).join(' › ') || 'raíz del sitio'}</p>
              </div>
            </div>

            {eliminando.tipo === 'carpeta' && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] font-normal leading-relaxed text-red-700 ring-1 ring-inset ring-red-200">
                ⚠️ Es una <strong>carpeta</strong>: se eliminará con <strong>todo su contenido</strong>.
              </p>
            )}
            {!zonaSegura() && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] font-normal leading-relaxed text-amber-800 ring-1 ring-inset ring-amber-200">
                ⚠️ Estás <strong>fuera de "Proyectos en desarrollo"</strong> — zona sensible del sitio de AMBEC.
              </p>
            )}
            <p className="text-[12.5px] font-light leading-relaxed text-gris">
              El elemento va a la papelera de SharePoint (recuperable por un administrador del sitio durante 93 días).
            </p>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirmar-nombre-sp" className="text-[13px] font-normal leading-relaxed text-texto">
                Para confirmar, escribe <strong className="select-all font-medium text-azul-noche">{eliminando.nombre}</strong>
              </label>
              <input
                id="confirmar-nombre-sp"
                type="text"
                value={nombreConfirm}
                onChange={(e) => setNombreConfirm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && nombreConfirm.trim() === eliminando.nombre && !borrandoSp) void eliminarItem(eliminando); }}
                placeholder={eliminando.nombre}
                autoComplete="off"
                spellCheck={false}
                autoFocus
                className={`w-full rounded-lg border px-3 py-2 text-[13px] text-texto placeholder:text-gris/40 focus:outline-none focus:ring-2 ${
                  nombreConfirm.trim() === eliminando.nombre
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-slate-200 focus:border-azul-profundo focus:ring-azul-profundo/20'
                }`}
              />
              {nombreConfirm.length > 0 && nombreConfirm.trim() !== eliminando.nombre && (
                <p className="text-[11.5px] font-light text-gris">El nombre no coincide todavía.</p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEliminando(null)}
                disabled={borrandoSp}
                className="rounded-lg px-3.5 py-2 text-[13px] font-normal text-texto transition-colors hover:bg-slate-100 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void eliminarItem(eliminando)}
                disabled={nombreConfirm.trim() !== eliminando.nombre || borrandoSp}
                className="rounded-lg bg-red-600 px-3.5 py-2 text-[13px] font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {borrandoSp ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
